import { createApi } from "@reduxjs/toolkit/query/react";
import {
  SkuReportsResult,
  SkuReportsSearch,
  SkuReports,
  OrderItemSearch,
  OrderItemSearchResult,
  OrderItemSummaryResult,
  MissingStockSearch,
  MissingStockResult,
} from "@/types/meesho/meeshoOrder";
import { marketPlaceBaseQueryWrapper } from "@/utils/rtkHelper";
import {
  UnAssignSkuSearch,
  UnAssignSkuSearchResult,
} from "@/types/UnAssignSku";

export const MeeshoOrderApi = createApi({
  reducerPath: "MeeshoOrderApi",
  baseQuery: marketPlaceBaseQueryWrapper,
  keepUnusedDataFor: 0,
  tagTypes: ["orderItem"],
  endpoints: (builder) => ({
    getDashboardCounts: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/meesho-order/dashboard-count/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTotalOrdersChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/meesho-order/total-order-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getChargeableAmtChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/meesho-order/chargeable-amounts/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getReturnStatusChartChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/meesho-order/return-status-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTop10SkuProfitChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/meesho-order/top-10-sku-profit-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTop10SkuLossChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/meesho-order/top-10-sku-loss-chart/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTop10SalesSkuChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/meesho-order/top-10-sales-sku/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTop10StateWiseCustomerReturnChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/meesho-order/top-10-customer-returns-state/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTop10SkuOfCustomerReturnChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/meesho-order/top-10-sku-of-customer-return/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTop10CityWiseSalesOrderChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/meesho-order/top-10-sales-order-city-wise/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTop10StateWiseSalesOrderChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/meesho-order/top-10-sales-order-state-wise/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getTop10CustomerReturnChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/meesho-order/top-10-customer-return/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getRealTimeProfitChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/meesho-order/real-time-profit/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getUpcomingProfitChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/meesho-order/upcoming-profit/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getNetProfitChart: builder.query({
      query: ({ startDate, endDate, skuId }) =>
        `/meesho-order/net-profit/?start_date=${startDate}&end_date=${endDate}&sku_id=${skuId}`,
      providesTags: ["orderItem"],
    }),
    getDropdownOptions: builder.query({
      query: () => `/meesho-order/sku/sku-dropdown`,
      providesTags: ["orderItem"],
    }),
    skuReportsMeeshoOrders: builder.query<SkuReportsResult, SkuReportsSearch>({
      query: (input) => ({
        url: `/meesho-order/meesho/sku-report`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["orderItem"],
    }),
    exportSkuReports: builder.mutation<Blob, SkuReportsSearch>({
      query: (input) => ({
        url: `/meesho-order/meesho/sku-report`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    summarySkuReport: builder.query<SkuReports, SkuReportsSearch>({
      query: (input) => ({
        url: `/meesho-order/sku-report/summary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["orderItem"],
    }),
    searchMeeshoOrders: builder.query<OrderItemSearchResult, OrderItemSearch>({
      query: (input) => ({
        url: `/meesho-order/ordersearch`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["orderItem"],
    }),
    exportMeeshoOrders: builder.mutation<Blob, OrderItemSearch>({
      query: (input) => ({
        url: `/meesho-order/ordersearch`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    summaryOrderItem: builder.query<OrderItemSummaryResult, OrderItemSearch>({
      query: (input) => ({
        url: `/meesho-order/ordersummary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["orderItem"],
    }),
    getUnAssignSku: builder.query<UnAssignSkuSearchResult, UnAssignSkuSearch>({
      query: (input) => ({
        url: `/meesho-order/unassigned-skus`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["orderItem"],
    }),
    exportUnAssignSku: builder.mutation<Blob, UnAssignSkuSearch>({
      query: (input) => ({
        url: `/meesho-order/unassigned-skus`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    getMissingStock: builder.query<MissingStockResult, MissingStockSearch>({
      query: (input) => ({
        url: `/meesho-order/missing-stock`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["orderItem"],
    }),
    exportMissingStock: builder.mutation<Blob, MissingStockSearch>({
      query: (input) => ({
        url: `/meesho-order/missing-stock`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    getMissingStockCount: builder.query<number, void>({
      query: () => `/meesho-order/missing_stock_count`,
      providesTags: ["orderItem"],
    }),
    getUniqueUnAssignSkus: builder.query<string[], void>({
      query: () => `/meesho-order/skus/unique-unassign-skus`,
      providesTags: ["orderItem"],
    }),
  }),
});

export const {
  useGetDashboardCountsQuery,
  useGetTotalOrdersChartQuery,
  useGetReturnStatusChartChartQuery,
  useGetChargeableAmtChartQuery,
  useGetTop10SkuProfitChartQuery,
  useGetTop10SkuLossChartQuery,
  useGetTop10SalesSkuChartQuery,
  useGetTop10StateWiseCustomerReturnChartQuery,
  useGetTop10SkuOfCustomerReturnChartQuery,
  useGetTop10CityWiseSalesOrderChartQuery,
  useGetTop10StateWiseSalesOrderChartQuery,
  useGetTop10CustomerReturnChartQuery,
  useGetDropdownOptionsQuery,
  useSkuReportsMeeshoOrdersQuery,
  useLazySkuReportsMeeshoOrdersQuery,
  useExportSkuReportsMutation,
  useSummarySkuReportQuery,
  useSearchMeeshoOrdersQuery,
  useLazySearchMeeshoOrdersQuery,
  useExportMeeshoOrdersMutation,
  useSummaryOrderItemQuery,
  useGetUnAssignSkuQuery,
  useLazyGetUnAssignSkuQuery,
  useExportUnAssignSkuMutation,
  useGetNetProfitChartQuery,
  useGetRealTimeProfitChartQuery,
  useGetUpcomingProfitChartQuery,
  useGetMissingStockQuery,
  useExportMissingStockMutation,
  useGetMissingStockCountQuery,
  useGetUniqueUnAssignSkusQuery,
  useLazyGetUniqueUnAssignSkusQuery,
} = MeeshoOrderApi;
